import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Maximize2, Minimize2, Loader2, Sparkles,
  Share2, Copy, Mail, MessageCircle, ExternalLink,
} from 'lucide-react';
import { SlideCanvas } from '../components/sales-deck/SlideCanvas';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Sales Deck — interactive presentation tool for the Operator's sales team.
 * Pick a business type → present a tailored 10-slide deck. Share dialog
 * produces a public link a prospect can open without logging in.
 */
export const SalesDeckPage = () => {
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState('');
  const [deck, setDeck] = useState(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [brand, setBrand] = useState({
    name: typeof window !== 'undefined' ? window.__KONNECT_BRAND_NAME : null,
    host: typeof window !== 'undefined' ? window.location.host : 'konnect.kaharian.com',
    accent: '#10b981',
    bookingUrl: null,
  });

  // Resolve the Operator's preferred share host: custom domain → subdomain → current host.
  // We always want links the prospect receives to land on the Operator's branded URL,
  // even if the Operator is currently logged in via konnect.kaharian.com.
  useEffect(() => {
    axios.get(`${API}/api/whitelabel/me`).then((r) => {
      const cfg = r.data?.config || {};
      const root = r.data?.subdomain_root || 'konnect.kaharian.com';
      let host = window.location.host;
      if (cfg.custom_domain && cfg.domain_status === 'verified') {
        host = cfg.custom_domain;
      } else if (cfg.subdomain_slug) {
        host = `${cfg.subdomain_slug}.${root}`;
      }
      setBrand({
        name: cfg.company_name || window.__KONNECT_BRAND_NAME || 'Konnect',
        host,
        accent: cfg.primary_color
          || getComputedStyle(document.documentElement).getPropertyValue('--brand-primary').trim()
          || '#10b981',
        bookingUrl: cfg.booking_url || null,
      });
    }).catch(() => { /* fall back to defaults */ });
  }, []);

  // SlideCanvas expects { name, domain, accent }
  const slideBrand = useMemo(() => ({
    name: brand.name,
    domain: brand.host,
    accent: brand.accent,
  }), [brand]);

  useEffect(() => {
    axios.get(`${API}/api/sales-deck/types`)
      .then(r => setTypes(r.data.types || []))
      .catch(() => toast.error('Could not load business types'));
  }, []);

  useEffect(() => {
    if (!typeId) { setDeck(null); return; }
    setLoading(true);
    setSlideIdx(0);
    axios.get(`${API}/api/sales-deck/${typeId}`)
      .then(r => setDeck(r.data))
      .catch(() => toast.error('Could not load deck'))
      .finally(() => setLoading(false));
  }, [typeId]);

  useEffect(() => {
    const onKey = (e) => {
      if (!deck) return;
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        setSlideIdx(i => Math.min(i + 1, deck.slides.length - 1));
      }
      if (e.key === 'ArrowLeft') setSlideIdx(i => Math.max(i - 1, 0));
      if (e.key === 'Escape' && fullscreen) setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deck, fullscreen]);

  const publicUrl = typeId ? `https://${brand.host}/p/deck/${typeId}` : '';

  return (
    <div className={fullscreen ? 'fixed inset-0 z-50 bg-white overflow-auto' : 'space-y-6 p-6'}>
      {!fullscreen && (
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight">Sales Deck Studio<PageInfoTooltip pageKey="sales-deck" /></h1>
            <p className="text-sm text-gray-medium mt-1">
              Tailored presentations for every business type. Pick a prospect type → present or share.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={typeId} onValueChange={setTypeId}>
              <SelectTrigger className="w-[300px]" data-testid="sales-deck-type-select">
                <SelectValue placeholder="Pick a prospect's business type…" />
              </SelectTrigger>
              <SelectContent className="max-h-[400px]">
                {types.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.emoji} {t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {deck && (
              <>
                <Button
                  variant="outline"
                  onClick={() => window.open(`https://${brand.host}/p/deck/${typeId}`, '_blank', 'noopener')}
                  data-testid="sales-deck-preview-as-prospect"
                  title="See exactly what your prospect sees when they open the link"
                >
                  <ExternalLink className="h-4 w-4 mr-2" /> Preview as Prospect
                </Button>
                <Button
                  variant="default"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => setShareOpen(true)}
                  data-testid="sales-deck-share"
                >
                  <Share2 className="h-4 w-4 mr-2" /> Share with prospect
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
        </div>
      )}

      {!loading && !deck && !typeId && (
        <Card className="border-dashed">
          <CardContent className="text-center py-20">
            <Sparkles className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-lg font-semibold">Pick a business type to start</h2>
            <p className="text-sm text-gray-medium mt-1 max-w-md mx-auto">
              Each type has a fully tailored 10-slide deck — pain points specific to that industry,
              the exact Konnect modules that solve them, and a recommended plan. You can present
              live or copy a public link to send to a prospect.
            </p>
          </CardContent>
        </Card>
      )}

      {deck && !loading && (
        <div>
          <SlideCanvas
            slide={deck.slides[slideIdx]}
            deckLabel={deck.label}
            brand={slideBrand}
            leadCapture={brand.bookingUrl ? { bookingUrl: brand.bookingUrl } : null}
          />

          <div className="mt-4 flex items-center justify-between bg-white rounded-lg border p-3 shadow-sm">
            <div className="text-sm text-gray-medium">
              Slide <strong>{slideIdx + 1}</strong> / {deck.slides.length}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => setSlideIdx(i => Math.max(i - 1, 0))}
                disabled={slideIdx === 0}
                data-testid="sales-deck-prev"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => setFullscreen(!fullscreen)}
                data-testid="sales-deck-fullscreen"
              >
                {fullscreen
                  ? <><Minimize2 className="h-4 w-4 mr-1" /> Exit fullscreen</>
                  : <><Maximize2 className="h-4 w-4 mr-1" /> Present</>}
              </Button>
              <Button
                size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setSlideIdx(i => Math.min(i + 1, deck.slides.length - 1))}
                disabled={slideIdx === deck.slides.length - 1}
                data-testid="sales-deck-next"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>

          {!fullscreen && (
            <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
              {deck.slides.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setSlideIdx(i)}
                  className={`shrink-0 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    i === slideIdx ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`sales-deck-jump-${i}`}
                >
                  {i + 1}. {s.title?.slice(0, 32) || s.kind}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        url={publicUrl}
        deckLabel={deck?.label}
      />
    </div>
  );
};

const ShareDialog = ({ open, onClose, url, deckLabel }) => {
  const copy = () => {
    navigator.clipboard?.writeText(url);
    toast.success('Link copied to clipboard.');
  };
  const waText = encodeURIComponent(
    `Hi! Here's a quick 10-slide overview of how Konnect helps ${deckLabel || 'businesses like yours'}: ${url}`
  );
  const emailSubject = encodeURIComponent(`Konnect — built for ${deckLabel || 'your business'}`);
  const emailBody = encodeURIComponent(
    `Hi,\n\nI put together a quick 10-slide deck tailored to ${deckLabel || 'your business'} — pain points, ` +
    `the modules that solve them, and a recommended plan.\n\nIt's a live page, no login needed: ${url}\n\n` +
    `Let me know which slide is most relevant and I'll set up a 15-minute walkthrough.\n\nThanks.`
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="sales-deck-share-dialog">
        <DialogHeader>
          <DialogTitle>Share this deck with your prospect</DialogTitle>
          <DialogDescription>
            Public, no-login link. The prospect sees the deck branded as your whitelabel.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            <Input value={url} readOnly className="font-mono text-sm" data-testid="sales-deck-share-url" />
            <Button variant="outline" onClick={copy} data-testid="sales-deck-share-copy">
              <Copy className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => window.open(url, '_blank')} data-testid="sales-deck-share-open">
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => window.open(`https://wa.me/?text=${waText}`, '_blank')}
              data-testid="sales-deck-share-whatsapp"
            >
              <MessageCircle className="h-4 w-4 mr-2 text-green-600" />
              Send via WhatsApp
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => window.open(`mailto:?subject=${emailSubject}&body=${emailBody}`, '_blank')}
              data-testid="sales-deck-share-email"
            >
              <Mail className="h-4 w-4 mr-2 text-blue-600" />
              Send via Email
            </Button>
          </div>

          <p className="text-xs text-gray-medium pt-2">
            Tip: share this from your <strong>own branded subdomain</strong> so the prospect sees your
            logo and company name in the footer, not Konnect&apos;s.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SalesDeckPage;
